"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui/button";
import { sendOtp, verifyOtp } from "@/lib/auth-api";
import { Toast } from "@/components/Toast";
import { getAuthToken, setAuthToken } from "@/lib/auth-token";

const SimplePageBackground = dynamic(
  () => import("@/components/SimplePageBackground").then((m) => m.SimplePageBackground),
  { ssr: true }
);
const IKFooter = dynamic(() => import("@/components/IKFooter").then((m) => m.IKFooter), { ssr: true });

const RESEND_COOLDOWN_SEC = 60;

function getApiErrorMessage(err: unknown): string {
  if (!err || typeof err !== "object") return "Something went wrong. Please try again.";
  const e = err as { message?: string; errors?: Record<string, string[]>; status?: number };
  if (e.message) return e.message;
  if (e.errors) {
    const all = Object.values(e.errors).flat();
    return all.join(" ");
  }
  if (e.status === 401) return "Invalid or expired OTP. Please try again.";
  if (e.status === 404) return "Mobile number not found. Please sign up first.";
  if (e.status === 422) return "Invalid input. Please check your details.";
  return "Something went wrong. Please try again.";
}

function isValidReturnTo(path: string | null): path is string {
  if (!path || typeof path !== "string") return false;
  if (path === "undefined" || path === "null" || path.includes("undefined")) return false;
  if (path === "/" || path === "/login" || path === "/signup") return false;
  return path.startsWith("/") && !path.startsWith("//");
}

function maskMobile(mobile: string): string {
  const digits = mobile.replace(/\D/g, "");
  if (digits.length < 4) return "****";
  return "******" + digits.slice(-4);
}

const inputBase =
  "w-full h-12 px-4 rounded-xl border-2 bg-white dark:bg-neutral-800 text-[#131222] dark:text-white placeholder:text-neutral-500 focus:ring-2 focus:ring-[#FFB51D]/25 outline-none";
const inputError = "border-red-500 dark:border-red-600";
const inputOk = "border-neutral-200 dark:border-neutral-600 focus:border-[#FFB51D]";

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams?.get("returnTo");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const token = getAuthToken();
      if (token) {
        const target = isValidReturnTo(returnTo) ? returnTo : "/customer";
        router.replace(target);
      }
    }
  }, [router, returnTo]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  useEffect(() => {
    if (otpSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [otpSent]);

  const doSendOtp = async () => {
    const mobileTrimmed = mobile.trim();
    const digits = mobileTrimmed.replace(/\D/g, "");
    if (!mobileTrimmed) {
      setFieldErrors({ mobile: "Mobile number is required." });
      return;
    }
    if (digits.length < 10) {
      setFieldErrors({ mobile: "Enter a valid 10-digit mobile number." });
      return;
    }

    setSendOtpLoading(true);
    setFieldErrors({});
    setToast(null);
    try {
      await sendOtp(mobileTrimmed);
      setOtpSent(true);
      setResendTimer(RESEND_COOLDOWN_SEC);
      setOtp("");
    } catch (err) {
      const e = err as { status?: number };
      if (e.status === 404) {
        // User not registered - redirect to signup with prefilled mobile
        if (typeof window !== "undefined") {
          sessionStorage.setItem("signup_prefill_mobile", mobileTrimmed);
        }
        router.push(isValidReturnTo(returnTo) ? `/signup?returnTo=${encodeURIComponent(returnTo)}` : "/signup");
        return;
      }
      // All other errors (401, 422, 500, etc.) - show on login page
      setToast(getApiErrorMessage(err));
    } finally {
      setSendOtpLoading(false);
    }
  };

  const handleSendOtp = (e: React.FormEvent) => {
    e.preventDefault();
    void doSendOtp();
  };

  const handleChangeNumber = () => {
    setOtpSent(false);
    setOtp("");
    setResendTimer(0);
    setFieldErrors({});
    setToast(null);
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setToast(null);

    const mobileTrimmed = mobile.trim();
    const otpTrimmed = otp.trim();
    const err: Record<string, string> = {};
    if (!otpTrimmed) err.otp = "OTP is required.";
    else if (otpTrimmed.length !== 4) err.otp = "Enter a 4-digit OTP.";
    if (Object.keys(err).length > 0) {
      setFieldErrors(err);
      return;
    }

    setLoading(true);
    try {
      const res = await verifyOtp(mobileTrimmed, otpTrimmed);
      if (res.success && res.data?.access_token) {
        const token = res.data.access_token;
        setAuthToken(token);
        const target = isValidReturnTo(returnTo) ? returnTo : "/customer";
        router.replace(target);
      }
    } catch (err) {
      setToast(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (resendTimer > 0) return;
    void doSendOtp();
  };

  const mobileValid = mobile.trim().replace(/\D/g, "").length >= 10;

  return (
    <SimplePageBackground>
      <div className="container mx-auto px-4 py-4 sm:py-6 min-h-[calc(100vh-4rem)] flex items-center">
        <div className="mx-auto w-full max-w-md">
          <div className="rounded-2xl bg-white dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 shadow-xl p-5 sm:p-6">
            <h1 className="text-xl sm:text-2xl font-bold text-[#131222] dark:text-white mb-2 text-center">
              Sign in
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4 text-sm text-center">
              If you don&apos;t have an account register
              <br />
              You can{" "}
              <Link
                href={returnTo ? `/signup?returnTo=${encodeURIComponent(returnTo)}` : "/signup"}
                className="font-bold text-[#FFB51D] hover:text-[#FFA500] transition-colors"
              >
                Register here!
              </Link>
            </p>

            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <label htmlFor="mobile" className="block text-sm font-medium text-[#131222] dark:text-white mb-2">
                  Phone Number*
                </label>
                <input
                  id="mobile"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="Enter 10-digit mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  disabled={otpSent}
                  className={`${inputBase} ${fieldErrors.mobile ? inputError : inputOk} disabled:opacity-70 disabled:cursor-not-allowed`}
                />
                {fieldErrors.mobile && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.mobile}</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={!mobileValid || sendOtpLoading || otpSent}
                className="w-full h-12 bg-gradient-to-r from-[#FFB51D] to-[#FFA500] hover:from-[#FFA500] hover:to-[#FFB51D] text-[#131222] font-bold disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {sendOtpLoading ? "Sending..." : "Send OTP"}
              </Button>
            </form>

            {otpSent && (
              <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    OTP sent to {maskMobile(mobile)}
                  </p>
                  <button
                    type="button"
                    onClick={handleChangeNumber}
                    className="text-sm font-semibold text-[#FFB51D] hover:text-[#FFA500] transition-colors"
                  >
                    Change
                  </button>
                </div>

                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-[#131222] dark:text-white mb-2">
                      OTP*
                    </label>
                    <input
                      ref={otpInputRef}
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="0000"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      maxLength={4}
                      className={`${inputBase} ${fieldErrors.otp ? inputError : inputOk}`}
                    />
                    {fieldErrors.otp && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.otp}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || otp.length !== 4}
                    className="w-full h-11 sm:h-12 bg-gradient-to-r from-[#FFB51D] to-[#FFA500] hover:from-[#FFA500] hover:to-[#FFB51D] text-[#131222] font-bold disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? "Signing in..." : "Sign in"}
                  </Button>

                  <div className="text-center">
                    {resendTimer > 0 ? (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Resend OTP in {resendTimer}s
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={sendOtpLoading}
                        className="text-sm font-semibold text-[#FFB51D] hover:text-[#FFA500] transition-colors disabled:opacity-70"
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      <IKFooter />
    </SimplePageBackground>
  );
}
